import { useRef, useState } from "react";
import { Upload, X, Download, AlertCircle, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";

/**
 * Reusable CSV import dialog.
 * Props :
 *   - kind : "users" | "salles" | "sessions"
 *   - open / onClose
 *   - onImported : (result) => void
 */
export default function CsvImportDialog({ kind, open, onClose, onImported }) {
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [running, setRunning] = useState(false);
    const [dryRun, setDryRun] = useState(true);
    const [result, setResult] = useState(null);
    const [template, setTemplate] = useState(null);

    const downloadTemplate = async () => {
        try {
            const { data } = await api.get(`/admin/csv-templates/${kind}`);
            setTemplate(data);
            const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = data.filename; a.click();
            URL.revokeObjectURL(url);
            toast.success("Template téléchargé");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const submit = async () => {
        if (!file) {
            toast.error("Sélectionnez un fichier CSV");
            return;
        }
        setRunning(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("dry_run", String(dryRun));
            const token = localStorage.getItem("cursus_physio_token");
            const r = await fetch(`${API}/admin/${kind}/import-csv`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || "Échec de l'import");
            setResult(data);
            toast.success(dryRun ? "Aperçu généré" : `Import effectué : ${data.created} créés, ${data.updated || 0} maj`);
            if (!dryRun) onImported?.(data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setRunning(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="csv-import-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-3xl bg-white shadow-premium-lg flex flex-col"
            >
                <div className="flex items-start justify-between border-b border-brand-line p-6">
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-orange">Import CSV</div>
                        <h3 className="mt-1 font-display text-2xl">Importer {kind === "users" ? "des utilisateurs" : kind === "salles" ? "des salles" : "des sessions"}</h3>
                    </div>
                    <button onClick={onClose} className="text-brand-smoke hover:text-brand-ink">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-auto p-6 space-y-5">
                    <div className="rounded-xl bg-[#FAFAFA] p-4">
                        <button
                            onClick={downloadTemplate}
                            data-testid="csv-download-template"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-orange hover:underline"
                        >
                            <Download className="h-3.5 w-3.5" /> Télécharger le template CSV
                        </button>
                        {template?.notes && (
                            <p className="mt-2 text-xs text-brand-smoke">{template.notes}</p>
                        )}
                    </div>

                    <div>
                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Fichier CSV</label>
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".csv,text/csv"
                            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                            data-testid="csv-file-input"
                            className="mt-2 w-full rounded-xl border border-brand-line bg-white p-3 text-sm file:mr-4 file:rounded-full file:border-0 file:bg-brand-orange file:px-4 file:py-2 file:text-xs file:font-semibold file:text-white"
                        />
                    </div>

                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={dryRun}
                            onChange={(e) => setDryRun(e.target.checked)}
                            data-testid="csv-dryrun"
                        />
                        Aperçu (dry-run) — ne crée rien, valide seulement
                    </label>

                    {result && (
                        <div className="space-y-3" data-testid="csv-result">
                            <div className="grid grid-cols-3 gap-2">
                                <div className="rounded-xl border border-green-200 bg-green-50 p-3">
                                    <div className="text-[10px] font-semibold uppercase text-green-700">Créés</div>
                                    <div className="mt-1 font-display text-2xl text-green-800">{result.created}</div>
                                </div>
                                <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                                    <div className="text-[10px] font-semibold uppercase text-blue-700">Mis à jour</div>
                                    <div className="mt-1 font-display text-2xl text-blue-800">{result.updated || 0}</div>
                                </div>
                                <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                                    <div className="text-[10px] font-semibold uppercase text-red-700">Erreurs</div>
                                    <div className="mt-1 font-display text-2xl text-red-800">{result.errors?.length || 0}</div>
                                </div>
                            </div>
                            {result.errors?.length > 0 && (
                                <div className="rounded-xl bg-red-50 p-4 max-h-40 overflow-auto">
                                    <ul className="space-y-1 text-xs text-red-900">
                                        {result.errors.map((e, i) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                                                <span>Ligne {e.line} : {e.message}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {result.credentials?.length > 0 && (
                                <div className="rounded-xl border border-brand-orange/30 bg-brand-orange/5 p-4">
                                    <div className="flex items-center gap-2 text-xs font-semibold text-brand-orange">
                                        <CheckCircle2 className="h-3.5 w-3.5" /> Comptes créés — mots de passe temporaires
                                    </div>
                                    <ul className="mt-2 space-y-1 text-xs">
                                        {result.credentials.map((c) => (
                                            <li key={c.email} className="flex justify-between">
                                                <span>{c.email}</span>
                                                <code className="font-semibold text-brand-ink">{c.temp_password}</code>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-end gap-3 border-t border-brand-line p-6">
                    <button onClick={onClose} className="rounded-full px-5 py-2.5 text-sm hover:bg-brand-line/30">Fermer</button>
                    <button
                        onClick={submit}
                        disabled={running || !file}
                        data-testid="csv-import-submit"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-50"
                    >
                        <Upload className="h-3.5 w-3.5" />
                        {running ? "En cours…" : (dryRun ? "Lancer l'aperçu" : "Importer")}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
